<script setup lang="ts">
import { computed } from "vue";
import UtilsService from "@/services/others/UtilsService";
import {PaymentFlowRange} from "@/targets/light/types";
import {useI18n} from "vue-i18n";

interface Emits {
	(e: 'selected'): void;
}

interface Props {
	type?: 'card' | 'sbp';
	ranges: PaymentFlowRange | null | undefined;
	currency?: 'RUB' | 'USD' | 'USDT' | 'KZT';
	loading?: boolean;
	isError?: boolean;
	selected?: boolean;
}

const emits = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
	type: 'card',
	currency: 'RUB',
	loading: false,
	isError: false,
	selected: false,
});

const { t } = useI18n();

const isDisabled = computed(() => !props.ranges);

const detailInfo = computed(() => {
	const description = t('light-payment.create.tabs.settings.any-agent');

	if (props.type === 'card') {
		return {
			icon: 'i-payment-card-svg',
			title: t('light-payment.create.tabs.classic-tab.card.title'),
			description
		}
	} else {
		return {
			icon: 'i-payment-sbp-svg',
			title: t('light-payment.create.tabs.classic-tab.sbp.title'),
			description
		}
	}
});

const handleSelectTab = () => {
	if (!isDisabled.value) {
		emits('selected');
	}
}

const utilsService = UtilsService.of();

const currencyFormatter = computed(() => utilsService.currencyFormatter(props.currency));

const text = computed(() => {
	if (!props.ranges?.min || !props.ranges?.max) return t('light-payment.create.tabs.settings.unavailable');

	if (props.ranges.min === props.ranges.max) {
		return `<span>${currencyFormatter.value.format(props.ranges.min)}</span>`;
	}

	return `<span>${currencyFormatter.value.format(props.ranges?.min)}</span> - <span>${currencyFormatter.value.format(props.ranges?.max)}</span>`;
});
</script>

<template>
	<f7-tab
		@click="handleSelectTab"
		:class="[
			'create-payment-tab',
			{ 'tab-disabled': isDisabled },
			{ 'tab-error': isError}
		]"
		:tab-active="selected"
	>
		<i :class="`create-payment-tab__icon ${detailInfo.icon}`" />
		<div class="create-payment-tab__body">
			<div class="create-payment-tab__title">{{ detailInfo.title }}</div>
			<div class="create-payment-tab__description">{{ detailInfo.description }}</div>
		</div>
		<div class="create-payment-tab__ranges">
			<template v-if="!loading">
				<div v-if="isDisabled">{{ t('light-payment.create.tabs.settings.unavailable') }}</div>
				<div v-else v-html="text" />
			</template>
			<template v-else>
				<div class="custom-loader">
					<span />
				</div>
			</template>
		</div>
	</f7-tab>
</template>

<style scoped lang="scss">
.create-payment-tab {
	$root: #{&};
	display: flex;
	flex-direction: column;
	align-items: center;
	outline: 1px solid #DBDDE4;
	border-radius: 0.5rem;
	padding: 0.75rem 0.5rem;
	background-color: #FFFFFF;
	cursor: pointer;

	&__icon {
		width: 2rem;
		height: 2rem;
	}

	&__body {
		margin-top: 0.5rem;
		display: flex;
		flex-direction: column;
		gap: .25rem
	}

	&__title {
		text-align: center;
		@include text(16px, 700, var(--brown1), 120%);
	}

	&__description {
		text-align: center;
		@include text(12px, 500, var(--grey2), 120%);
	}

	&__ranges {
		margin-top: 1rem;

		@include text(12px, 500, var(--grey2), 120%);

		span {
			text-wrap: nowrap;
		}
	}

	&.tab-disabled {
		background-color: var(--grey7);
		outline-color: transparent;
		pointer-events: none;
		opacity: 0.5;
		cursor: default;
	}

	&.tab-active {
		outline-color: #007AFF;
		pointer-events: none;
		outline-width: 2px;
	}

	&.tab-error {
		#{$root}__ranges {
			color: var(--red);
		}
	}
}
</style>
